import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";

const normaliseOption = (option) =>
  typeof option === "string" || typeof option === "number"
    ? { value: String(option), label: String(option) }
    : option;

export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "All",
  disabled = false,
  closeOnScroll = false,
  viewportTop = 0,
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const normalisedOptions = useMemo(
    () => options.map(normaliseOption).filter(Boolean),
    [options],
  );
  const selected = normalisedOptions.find((option) => option.value === value);
  const filteredOptions = normalisedOptions.filter((option) =>
    option.label.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target) &&
          !menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const reposition = (event) => {
      if (event?.target instanceof Node && menuRef.current?.contains(event.target)) return;
      if (event?.type === 'scroll' && closeOnScroll) { setOpen(false); return; }
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (viewportTop > 0 && rect.top < viewportTop) { setOpen(false); return; }
      const height = window.innerHeight;
      const width = Math.min(rect.width, window.innerWidth - 16);
      const below = height - rect.bottom - 12;
      const above = rect.top - viewportTop - 12;
      const upward = below < 280 && above > below;
      const maxHeight = Math.min(320, Math.max(0, upward ? above : below));
      setPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width,
        maxHeight,
        ...(upward ? { bottom: height - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, closeOnScroll, viewportTop]);

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative" onKeyDown={(event) => { if (event.key === "Escape" && open) { event.stopPropagation(); setOpen(false); triggerRef.current?.focus(); } }}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-left text-sm text-slate-800 outline-none transition hover:border-primary-300 focus:border-primary-700 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-slate-100"
      >
        <span className={`truncate ${selected ? "" : "text-slate-400"}`}>
          {selected?.label || placeholder}
        </span>
        <FiChevronDown className={`ml-2 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && position && createPortal(
        <div ref={menuRef} style={position} className="fixed z-[1000] flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl" data-report-select-menu>
          <div className="border-b border-slate-100 p-2">
            <label className="relative block">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search options"
                className="w-full rounded-md border border-slate-200 py-2 pl-8 pr-8 text-sm outline-none focus:border-primary-700"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiX />
                </button>
              )}
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1" style={{ overscrollBehavior: "contain" }}>
            <button type="button" onClick={() => choose("")} className={`block w-full px-3 py-2 text-left text-sm hover:bg-primary-50 ${!value ? "bg-primary-50 font-semibold text-primary-800" : "text-slate-700"}`}>{placeholder}</button>
            {filteredOptions.map((option) => <button type="button" key={option.value} onClick={() => choose(option.value)} className={`block w-full px-3 py-2 text-left text-sm hover:bg-primary-50 ${option.value === value ? "bg-primary-50 font-semibold text-primary-800" : "text-slate-700"}`}>{option.label}</button>)}
            {!filteredOptions.length && <p className="px-3 py-4 text-center text-sm text-slate-500">No matching options.</p>}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
